import { requireOrdinaryEntityType } from '@einhaus/project314-shared';
import type {
    GovernanceCapReachedMode,
    GovernanceContentionMode,
    EntityTypeUnion,
    GovernanceExposureKeyGrain,
    GovernanceOppositeDirectionMode,
    GovernanceSameDirectionMode,
    GovernanceSelectionMetric,
    PortfolioGovernanceEntityLimits,
    PortfolioGovernanceFamilyLimits,
    PortfolioGovernanceStrategyLimits,
    ResolvedPortfolioGovernancePolicy
} from '@einhaus/project314-types';

/** A single per-entity-type limit row as edited in the UI draft. */
export interface GovernanceEntityLimitDraft extends PortfolioGovernanceEntityLimits {
    entityTypeScope: EntityTypeUnion['entityType'] | 'All';
}

/** A single per-strategy-family limit row as edited in the UI draft. */
export type GovernanceFamilyLimitDraft = PortfolioGovernanceFamilyLimits;

/** A single per-strategy limit row as edited in the UI draft. */
export type GovernanceStrategyLimitDraft = PortfolioGovernanceStrategyLimits;

/** The full editable governance policy draft held in component state until saved. */
export interface GovernanceDraft {
    exposureKeyGrain: GovernanceExposureKeyGrain;
    sameDirectionAdmissionMode: GovernanceSameDirectionMode;
    oppositeDirectionMode: GovernanceOppositeDirectionMode;
    capReachedMode: GovernanceCapReachedMode;
    contentionMode: GovernanceContentionMode;
    selectionMetric: GovernanceSelectionMetric;
    requiresSharedScorecard: boolean;
    contentionWindowMs: number | null;
    /** Minimum improvement a candidate must clear before it may replace an existing position (anti-churn). */
    replacementThresholdPercent: number | null;
    entityLimits: GovernanceEntityLimitDraft[];
    /** Aggregate caps applied across all variants in selected strategy families. */
    familyLimits: GovernanceFamilyLimitDraft[];
    /** Aggregate caps applied to individual subscribed strategies across all entities. */
    strategyLimits: GovernanceStrategyLimitDraft[];
    /** Per-strategy ranking priority (strategyId → priority; lower ranks first). */
    subscriptionPriorities: Record<number, number>;
}

/** Default subscription priority for a strategy with no explicit ranking (matches the evaluator's fallback). */
export const DEFAULT_SUBSCRIPTION_PRIORITY = 100;

/** Whether a draft uses any replacement mode (so the editor can reveal the threshold control). */
export const usesReplacement = (draft: Pick<GovernanceDraft, 'oppositeDirectionMode' | 'capReachedMode'>): boolean =>
    draft.oppositeDirectionMode === 'closeOpposing' ||
    draft.oppositeDirectionMode === 'replaceIfBetter' ||
    draft.capReachedMode === 'replaceWeakestIfBetter';

/** Stable name persisted with the policy (the UI exposes one governance policy per portfolio). */
export const GOVERNANCE_POLICY_NAME = 'Portfolio governance policy';

/** Every entity-type scope that can carry its own limit row, ordered for display. */
export const GOVERNANCE_ENTITY_TYPE_SCOPES: GovernanceEntityLimitDraft['entityTypeScope'][] = [
    'All',
    'Asset',
    'Cryptocurrency',
    'AssetIndex'
];

export const EXPOSURE_GRAIN_OPTIONS: { value: GovernanceExposureKeyGrain; label: string }[] = [
    { value: 'underlying', label: 'Underlying — one symbol, any instrument' },
    { value: 'instrument', label: 'Instrument — symbol plus derivative' },
    { value: 'leg', label: 'Leg — exact contract / option leg' }
];

export const SAME_DIRECTION_OPTIONS: { value: GovernanceSameDirectionMode; label: string }[] = [
    { value: 'blockDuplicates', label: 'Block duplicates — one position per direction' },
    { value: 'allowStackingUpToLimit', label: 'Allow stacking up to the per-entity limit' }
];

export const OPPOSITE_DIRECTION_OPTIONS: { value: GovernanceOppositeDirectionMode; label: string }[] = [
    { value: 'blockNew', label: 'Block — reject the opposing trade' },
    { value: 'allowHedge', label: 'Allow hedge — permit long and short together' },
    { value: 'manualReview', label: 'Manual review — route opposing trade for approval' },
    { value: 'closeOpposing', label: 'Close opposing — flip: close the old position, open the new' },
    { value: 'replaceIfBetter', label: 'Replace if better — close opposing only when the new trade wins' }
];

export const CAP_REACHED_OPTIONS: { value: GovernanceCapReachedMode; label: string }[] = [
    { value: 'blockNew', label: 'Block — reject once the per-entity cap is reached' },
    { value: 'replaceWeakestIfBetter', label: 'Replace weakest if better — evict the weakest position for a stronger one' }
];

export const CONTENTION_OPTIONS: { value: GovernanceContentionMode; label: string }[] = [
    { value: 'keepHighestRankedOnly', label: 'Keep highest ranked only (one winner per entity)' },
    { value: 'acceptAllWithinLimits', label: 'Accept all within limits' }
];

export const SELECTION_METRIC_OPTIONS: { value: GovernanceSelectionMetric; label: string }[] = [
    { value: 'predictedProfit', label: 'Predicted profit (requires shared scorecard)' },
    { value: 'confidence', label: 'Entry confidence / score' },
    { value: 'strategyPriority', label: 'Strategy subscription priority' },
    { value: 'firstCome', label: 'First come, first served' }
];

export const ENTITY_TYPE_SCOPE_LABELS: Record<EntityTypeUnion['entityType'] | 'All', string> = {
    All: 'All entity types',
    Asset: 'Stocks',
    Cryptocurrency: 'Crypto',
    AssetIndex: 'Indices'
};

/** A new per-scope limit row with permissive defaults (no hard caps, all exposure sources counted). */
export const buildDefaultEntityLimit = (scope: EntityTypeUnion['entityType'] | 'All'): GovernanceEntityLimitDraft => ({
    entityTypeScope: scope,
    maxPositionsPerEntity: 1,
    maxSameDirectionTradesPerEntity: null,
    maxOppositeDirectionTradesPerEntity: null,
    maxEntityRiskCapitalPercent: null,
    includePendingOrders: true,
    includeQueuedTrades: true,
    includeManualApprovalPending: true
});

/** A new per-family limit row with all exposure sources counted. */
export const buildDefaultFamilyLimit = (familyId: number): GovernanceFamilyLimitDraft => ({
    familyId,
    maxOpenPositionsPerFamily: 2,
    maxFamilyRiskCapitalPercent: null,
    includePendingOrders: true,
    includeQueuedTrades: true,
    includeManualApprovalPending: true
});

/** A new per-strategy limit row with all exposure sources counted. */
export const buildDefaultStrategyLimit = (strategyId: number): GovernanceStrategyLimitDraft => ({
    strategyId,
    maxOpenPositionsPerStrategy: 15,
    includePendingOrders: true,
    includeQueuedTrades: true,
    includeManualApprovalPending: true
});

/** A fresh governance draft used when enabling governance on a portfolio that has no active policy. */
export const buildDefaultGovernanceDraft = (): GovernanceDraft => ({
    exposureKeyGrain: 'underlying',
    sameDirectionAdmissionMode: 'blockDuplicates',
    oppositeDirectionMode: 'blockNew',
    capReachedMode: 'blockNew',
    contentionMode: 'keepHighestRankedOnly',
    selectionMetric: 'predictedProfit',
    requiresSharedScorecard: true,
    contentionWindowMs: null,
    replacementThresholdPercent: null,
    entityLimits: [buildDefaultEntityLimit('All')],
    familyLimits: [],
    strategyLimits: [],
    subscriptionPriorities: {}
});

/** Builds an editable draft from a resolved active policy loaded from the server. */
export const buildDraftFromPolicy = (policy: ResolvedPortfolioGovernancePolicy): GovernanceDraft => {
    const entityLimits = GOVERNANCE_ENTITY_TYPE_SCOPES.reduce<GovernanceEntityLimitDraft[]>((acc, scope) => {
        const limits = policy.limitsByScope[scope];

        if (limits) {
            acc.push({ entityTypeScope: scope === 'All' ? scope : requireOrdinaryEntityType(scope), ...limits });
        }

        return acc;
    }, []);

    return {
        exposureKeyGrain: policy.exposureKeyGrain,
        sameDirectionAdmissionMode: policy.sameDirectionAdmissionMode,
        oppositeDirectionMode: policy.oppositeDirectionMode,
        capReachedMode: policy.capReachedMode,
        contentionMode: policy.contentionMode,
        selectionMetric: policy.selectionMetric,
        requiresSharedScorecard: policy.requiresSharedScorecard,
        contentionWindowMs: policy.contentionWindowMs,
        replacementThresholdPercent: policy.replacementThresholdPercent,
        entityLimits: entityLimits.length > 0 ? entityLimits : [buildDefaultEntityLimit('All')],
        familyLimits: Object.values(policy.familyLimitsByFamilyId),
        strategyLimits: Object.values(policy.strategyLimitsByStrategyId),
        subscriptionPriorities: { ...policy.subscriptionPriorityByStrategyId }
    };
};
